/**
 * useEventStateCertainty — the one place the trading surfaces get their
 * event-state trust level from (#481).
 *
 * Game Detail and Position Tracker warning surfaces read through this module,
 * so cards, compact rows, tables, and the replacement modal share one trust
 * contract instead of independently interpreting the same event.
 *
 * ── What it reads, and what it deliberately does not ────────────────
 * It consumes `gameStatesStore` for both the snapshot's freshness and the
 * exact game's normalized status record. The same Hydra-backed cache already
 * drives the dashboard's score and LIVE/FINAL presentation; using it here
 * removes the old split where the table could say LIVE while the adjacent
 * trading surface claimed that no source could confirm the event.
 *
 * ── The asymmetry that follows, stated plainly ──────────────────────
 * The trading warning and the display chip now share one allow-list
 * (`trustedCanonicalState`) and one staleness gate. A missing record remains
 * unknown and an aged snapshot remains stale; this does not turn schedule
 * arithmetic into authority.
 *
 * ── Which surfaces actually poll ────────────────────────────────────
 * `ValueDashboard` already polls the union of visible game keys.
 * `PositionTracker` supplies the union of its open-order game keys while that
 * route is mounted. The routes are mutually exclusive within a tab, so there
 * is still one poll owner and `setStates` can keep replacing its requested map
 * wholesale without two key sets clobbering one another.
 *
 * ── No timer, one subscription ──────────────────────────────────────
 * `lastAttemptMs` is used as "now", exactly as `GameTable` does: it
 * advances on every poll attempt, so the transition into `stale` happens
 * on the poll's own tick with no per-surface interval. The whole
 * derivation lives INSIDE the selector so these components re-render
 * when the answer flips, not on every 5s poll tick — subscribing to the
 * three raw fields would have put a 5s re-render on the heaviest
 * component in the app.
 */

import { useMemo } from 'react';
import { useGameStateFor, useGameStatesStore } from '@/stores/gameStatesStore';
import { primaryGameStateEnabled, readPrimaryGameState, usePrimaryGameState } from '@/services/primaryGameState';
import { lookupCoreGameState, useCoreGameStatesStore } from '@/services/coreGameStates';
import { isGameStateFeedStale } from '@/lib/sport/liveDisplayState';
import { deriveGameStateWithAuthority, useMarketStore } from '@/stores/marketStore';
import { baseGameKey, toHydraGameKeyLive } from '@/services/analyticsGameKey';
import {
  resolveEventStateCertainty,
  type EventStateAuthority,
  type EventStateCertainty,
  type FeedSnapshotState,
} from '@/lib/sport/eventStateCertainty';
import type { RowGameState } from '@/lib/sport/liveDisplayState';
import type { CanonicalGameStateInput } from '@/lib/sport/liveDisplayState';

export function useEventStateFeedSnapshotState(): FeedSnapshotState {
  const core = useCoreGameStatesStore<FeedSnapshotState>(s => s.status === 'live' ? 'current'
    : s.records.size ? 'stale' : 'absent');
  const legacy = useGameStatesStore<FeedSnapshotState>((s) => {
    // Never a successful fetch — a cold start, a poll that has only ever
    // failed, or a surface nothing polls for. "The feed has been failing
    // and its snapshot is too old" would be a false account of all three.
    if (s.lastFetchMs === 0) return 'absent';
    return isGameStateFeedStale(s.error, s.lastFetchMs, s.lastAttemptMs) ? 'stale' : 'current';
  });
  return primaryGameStateEnabled() ? core : legacy;
}

export function useEventStateCertainty(
  rowGameState: RowGameState,
  rowAuthority: EventStateAuthority = 'unknown',
  gameKey?: string | null,
): EventStateCertainty {
  const feed = useEventStateFeedSnapshotState();
  const canonical = useCanonicalGameState(gameKey);

  return resolveEventStateCertainty({ canonical, rowGameState, rowAuthority, feed });
}

/** Lifecycle signature for a set of exact game keys, for surfaces that resolve
 *  order certainty imperatively (`resolveOrderEventStateCertainty`) and only
 *  need to know WHEN to resolve again. It changes when a key's lifecycle
 *  authority can change — status, score presence and, on Core, the record's
 *  withdrawal, staleness, dispute, final reason and the connection — never on
 *  an ordinary score tick. Reads whichever store the primary switch reads:
 *  in Core primary mode nothing feeds the legacy store, so a legacy-only
 *  signature would never move. */
export function useGameStateLifecycleSignature(keys: readonly string[]): string {
  const legacy = useGameStatesStore((s) =>
    keys.map((key) => {
      const state = s.byGameKey.get(key);
      return state
        ? `${key}:${state.statusId}:${state.awayScore != null ? 1 : 0}:${state.homeScore != null ? 1 : 0}`
        : `${key}:absent`;
    }).join('|'),
  );
  const primary = primaryGameStateEnabled();
  const core = useCoreGameStatesStore((s) => !primary ? '' : `${s.status}|${keys.map((key) => {
    const hit = lookupCoreGameState(s.records, key);
    const row = hit?.row;
    const state = row && !row.withdrawal && row.state?.gameKey === hit?.coreKey ? row.state : null;
    return state
      ? `${key}:${state.statusId}:${state.awayScore != null ? 1 : 0}:${state.homeScore != null ? 1 : 0}`
        + `:${state.finalReason ?? '-'}:${state.stale ? 1 : 0}:${state.disputed ? 1 : 0}`
      : `${key}:${row?.withdrawal ?? 'absent'}`;
  }).join('|')}`);
  return primary ? core : legacy;
}

/** Read the exact normalized record shared by display and warning surfaces. */
export function useCanonicalGameState(gameKey: string | null | undefined) {
  const liveGameKey = useMemo(
    () => toHydraGameKeyLive(baseGameKey(gameKey)),
    [gameKey],
  );
  const legacy = useGameStateFor(liveGameKey);
  const primary = usePrimaryGameState(liveGameKey);
  return primaryGameStateEnabled() ? primary : legacy;
}

/** Resolve the exact live game-state key for a Kalshi ticker or Poly token. */
export function gameStateKeyForTickerOrToken(
  tickerOrToken: string | null | undefined,
): string | null {
  if (!tickerOrToken || typeof useMarketStore.getState !== 'function') return null;
  const store = useMarketStore.getState();
  const ticker = store.getMarket(tickerOrToken)
    ? tickerOrToken
    : (
      store.getTickerByTokenId(tickerOrToken)
      ?? store.getTickerByConditionId(tickerOrToken)
      ?? tickerOrToken
    );
  return typeof store.getGameKeyV2ForTicker === 'function'
    ? store.getGameKeyV2ForTicker(ticker)
    : null;
}

export function resolveOrderEventStateCertainty(
  tickerOrToken: string | null | undefined,
  feed: FeedSnapshotState,
  nowMs = Date.now(),
  canonicalOverride?: CanonicalGameStateInput | null,
): EventStateCertainty {
  if (!tickerOrToken) {
    return resolveEventStateCertainty({
      canonical: null,
      rowGameState: 'pre',
      rowAuthority: 'unknown',
      feed,
    });
  }

  // A few component tests replace the Zustand hook with a selector-only
  // double. Missing imperative state in that harness means evidence is
  // unavailable, never permission to guess a lifecycle.
  const store = typeof useMarketStore.getState === 'function'
    ? useMarketStore.getState()
    : null;
  if (!store) {
    return resolveEventStateCertainty({
      canonical: null,
      rowGameState: 'pre',
      rowAuthority: 'unknown',
      feed,
    });
  }
  const ticker = store.getMarket(tickerOrToken)
    ? tickerOrToken
    : (
      store.getTickerByTokenId(tickerOrToken)
      ?? store.getTickerByConditionId(tickerOrToken)
      ?? tickerOrToken
    );
  const market = store.getMarket(ticker);
  if (!market) {
    return resolveEventStateCertainty({
      canonical: null,
      rowGameState: 'pre',
      rowAuthority: 'unknown',
      feed,
    });
  }

  const parsedStart = market.eventStartTime ? Date.parse(market.eventStartTime) : NaN;
  const startTimeMs = Number.isFinite(parsedStart) ? parsedStart : null;
  const derived = deriveGameStateWithAuthority(
    market.status ? [market.status] : [],
    false,
    startTimeMs,
    nowMs,
    market.sport ?? null,
  );
  const gameKey = typeof store.getGameKeyV2ForTicker === 'function'
    ? store.getGameKeyV2ForTicker(ticker)
    : null;
  const canonical = canonicalOverride === undefined
    ? (primaryGameStateEnabled() ? readPrimaryGameState(gameKey)
      : gameKey ? useGameStatesStore.getState().getState(gameKey) : null)
    : canonicalOverride;
  return resolveEventStateCertainty({
    canonical,
    rowGameState: derived.gameState,
    rowAuthority: derived.authority,
    feed,
  });
}

export function useOrderEventStateCertainty(
  tickerOrToken: string | null | undefined,
): EventStateCertainty {
  const feed = useEventStateFeedSnapshotState();
  const marketVersion = useMarketStore((s) => s.version);
  const gameKey = useMemo(
    () => {
      void marketVersion;
      return gameStateKeyForTickerOrToken(tickerOrToken);
    },
    [marketVersion, tickerOrToken],
  );
  const canonical = useCanonicalGameState(gameKey);

  return resolveOrderEventStateCertainty(tickerOrToken, feed, undefined, canonical);
}
